import React, { useState, useEffect } from 'react';
import { Lock, ShieldCheck, Eye, EyeOff, FileText, Check } from 'lucide-react';
import { API_BASE } from '../apiConfig';

const styles = {
  container: {
    padding: '20px 0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '32px',
  },
  previewCard: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '16px',
    padding: '16px',
    width: '100%',
    maxWidth: '320px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
  },
  thumbnail: {
    width: '100%',
    aspectRatio: '1 / 1.41',
    background: 'var(--surface2)',
    borderRadius: '8px',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    border: '1px solid var(--border)',
  },
  thumbImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  fileName: {
    fontSize: '14px',
    fontWeight: 600,
    color: 'var(--text)',
    textAlign: 'center',
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  setupSection: {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '24px',
    padding: '32px',
    width: '100%',
    maxWidth: '480px',
    boxShadow: 'var(--shadow-lg)',
    position: 'relative',
    overflow: 'hidden',
  },
  setupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '24px',
  },
  setupIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    background: 'rgba(59, 130, 246, 0.1)',
    color: '#3b82f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  setupTitle: {
    fontSize: '18px',
    fontWeight: 700,
    color: 'var(--text)',
  },
  inputGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--text-muted)',
    marginBottom: '8px',
    marginLeft: '4px',
  },
  inputWrapper: {
    position: 'relative',
    width: '100%',
  },
  input: {
    width: '100%',
    height: '52px',
    background: 'var(--surface2)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    padding: '0 48px 0 16px',
    fontSize: '16px',
    color: 'var(--text)',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    outline: 'none',
  },
  inputFocus: {
    borderColor: '#3b82f6',
    boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.1)',
  },
  eyeBtn: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    color: 'var(--text-muted)',
    cursor: 'pointer',
    padding: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  requirements: {
    marginTop: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  reqItem: (met) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12px',
    color: met ? '#10b981' : 'var(--text-muted)',
    transition: 'color 0.2s',
  }),
  reqDot: (met) => ({
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: met ? '#10b981' : 'var(--border-hi)',
  })
};

export default function PDFProtect({ file, onPasswordChange }) {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [thumb, setThumb] = useState(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    let active = true;
    const fetchThumb = async () => {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('page', 0);
        const res = await fetch(`${API_BASE}/render-pdf`, { method: 'POST', body: formData });
        if (res.ok && active) {
          const blob = await res.blob();
          setThumb(URL.createObjectURL(blob));
        }
      } catch (e) {}
    };
    fetchThumb();
    return () => { active = false; if (thumb) URL.revokeObjectURL(thumb); };
  }, [file]);

  const handlePass = (val) => {
    setPassword(val);
    onPasswordChange(val);
  };

  const reqs = [
    { label: 'At least 4 characters', met: password.length >= 4 },
    { label: 'Strongly recommended for security', met: password.length >= 8 },
  ];

  return (
    <div style={styles.container}>
      <div style={styles.previewCard}>
        <div style={styles.thumbnail}>
          {thumb ? (
            <img src={thumb} alt="Preview" style={styles.thumbImg} />
          ) : (
            <FileText size={48} color="var(--border-hi)" />
          )}
        </div>
        <div style={styles.fileName}>{file.name}</div>
      </div>

      <div style={styles.setupSection}>
        <div style={styles.setupHeader}>
          <div style={styles.setupIcon}>
            <ShieldCheck size={24} />
          </div>
          <div style={styles.setupTitle}>Secure Your PDF</div>
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label}>Set Document Password</label>
          <div style={styles.inputWrapper}>
            <input 
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => handlePass(e.target.value)}
              placeholder="Enter password..."
              style={{
                ...styles.input,
                ...(isFocused ? styles.inputFocus : {})
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
            />
            <button 
              style={styles.eyeBtn}
              onClick={() => setShowPassword(!showPassword)}
              type="button"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          
          <div style={styles.requirements}>
            {reqs.map((r, i) => (
              <div key={i} style={styles.reqItem(r.met)}>
                {r.met ? <Check size={12} strokeWidth={3} /> : <div style={styles.reqDot(r.met)} />}
                <span>{r.label}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div style={{ 
          fontSize: '12px', 
          color: 'var(--text-muted)', 
          lineHeight: 1.5,
          marginTop: '8px',
          background: 'var(--surface2)',
          padding: '12px',
          borderRadius: '10px',
          border: '1px solid var(--border)'
        }}>
          <strong>Note:</strong> We use industry-standard <strong>AES-256 encryption</strong>. Once protected, only those with the password can view or edit this file.
        </div>
      </div>
    </div>
  );
}
